import React, { useState, useRef, useEffect } from 'react'
import {
  Paper, Typography, Stack, Chip, IconButton, Alert, TextField,
  Collapse,
} from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import DeleteIcon from '@mui/icons-material/Delete'
import { FS, RAIL_WIDTH, SP, V2 } from '../../theme/tokens'

// ── Role colour map ─────────────────────────────────────────────────────────

const ROLE_COLORS = {
  customer: V2.info100,
  samuel:   '#86efac',
}

const roleBg = (role) => {
  const c = ROLE_COLORS[role] ?? ROLE_COLORS.customer
  // 8 % opacity tint → hex 14 (≈ 0.08 × 255)
  return c + '14'
}

// ── ToneSegmentCard ─────────────────────────────────────────────────────────

export default function ToneSegmentCard({
  segment,
  index,
  flag,
  comment,
  dragHandleProps,
  onSwapRole,
  onDelete,
  onComment,
}) {
  const role = (segment.role ?? 'customer').toLowerCase()
  const borderColor = ROLE_COLORS[role] ?? ROLE_COLORS.customer

  // Comment field: visible when prop has text OR user toggled it open
  const [commentOpen, setCommentOpen] = useState(Boolean(comment))
  const [localComment, setLocalComment] = useState(comment ?? '')
  const prevComment = useRef(comment ?? '')

  // Sync external comment prop changes
  useEffect(() => {
    setLocalComment(comment ?? '')
    if (comment) setCommentOpen(true)
  }, [comment])

  const handleCommentBlur = () => {
    if (localComment !== prevComment.current) {
      prevComment.current = localComment
      onComment(index, localComment)
    }
  }

  const toggleComment = () => {
    setCommentOpen((o) => !o)
  }

  const roleLabel = role === 'samuel' ? 'Samuel' : 'Customer'

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderLeft: `${RAIL_WIDTH}px solid ${borderColor}`,
        bgcolor: roleBg(role),
        p: SP.sm,
      }}
    >
      {/* ── Header row ──────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={SP.chip}>
        {/* Drag handle */}
        <span {...dragHandleProps} style={{ display: 'flex', cursor: 'grab' }}>
          <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
        </span>

        <Chip
          label={roleLabel}
          size="small"
          sx={{
            bgcolor: borderColor,
            color: '#000',
            fontWeight: 600,
            fontSize: FS.xs,
            height: 22,
          }}
        />

        {segment.sender_email && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: FS.xs }}>
            {segment.sender_email}
          </Typography>
        )}

        <span style={{ flex: 1 }} />

        {/* Action buttons */}
        <IconButton size="small" onClick={() => onSwapRole(index)} title="Swap role">
          <SwapHorizIcon sx={{ fontSize: 16 }} />
        </IconButton>

        <IconButton
          size="small"
          onClick={toggleComment}
          title="Toggle comment"
          color={comment ? 'primary' : 'default'}
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
        </IconButton>

        <IconButton size="small" onClick={() => onDelete(index)} title="Delete segment" color="error">
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>

      {/* ── Parsing flag ────────────────────────────────────────── */}
      {flag && (
        <Alert
          severity="warning"
          sx={{ mt: SP.chip, py: 0, fontSize: FS.xs, '& .MuiAlert-message': { py: 0.25 } }}
        >
          {flag.detail} — {Math.round(flag.confidence * 100)}%
        </Alert>
      )}

      {/* ── Message body ────────────────────────────────────────── */}
      <Typography
        variant="body2"
        sx={{
          mt: SP.tight,
          fontSize: FS.sm,
          whiteSpace: 'pre-wrap',
          lineHeight: 1.5,
          maxHeight: 300,
          overflowY: 'auto',
        }}
      >
        {segment.body}
      </Typography>

      {/* ── Comment field (expandable) ──────────────────────────── */}
      <Collapse in={commentOpen}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={3}
          placeholder="Staff note for this segment…"
          value={localComment}
          onChange={(e) => setLocalComment(e.target.value)}
          onBlur={handleCommentBlur}
          sx={{ mt: SP.tight }}
        />
      </Collapse>
    </Paper>
  )
}
